import React, { useEffect, useMemo, useRef } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { type AnchorageArea, type PortCode } from "@services/vessel-call/types";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { FORM_FIELDS } from "../../constants";
import type { VesselCallOutletContext } from "../../CreateVesselCallContainer";
import type { VesselCallFormData } from "../../schema/vesselCallFormSchema";

const LANG_BASE = "vessel-call.create";

const LINKED_PORT_FIELDS = [
  FORM_FIELDS.EXPECTED_ARRIVAL_LOCATION,
  FORM_FIELDS.PREFERRED_ANCHORAGE_AREA,
  FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE,
  FORM_FIELDS.PORT_FACILITY,
  FORM_FIELDS.PORT_FACILITY_OTHER,
];

const anchorageOptions = ["FOAA", "DOAA"];

const Port: React.FC = () => {
  const {
    formData,
    updateFormData,
    watch,
    isFinalSubmission,
    prefillFromId,
    isRejectedFinalArrivalUpdate,
  } = useOutletContext<VesselCallOutletContext>();
  const { isMarineDepartmentUser, user } = useAuthStore();

  const userPort = user?.port;

  const selectedPort = watch(FORM_FIELDS.SELECT_PORT);
  const expectedArrivalLocation = watch(FORM_FIELDS.EXPECTED_ARRIVAL_LOCATION);

  const { options: ports } = useFormattedReference<PortCode>(
    REFERENCE_DATA_TYPES.PORT_CODES,
    "bff-vessel-call",
  );

  const portsOptions = useMemo(
    () => ports.map((p) => ({ label: p.portName, value: p.portCode })),
    [ports],
  );

  const userPortOption = useMemo(() => {
    if (!userPort) return null;

    return (
      portsOptions.find(
        (portOption) => portOption.value.toLocaleLowerCase() === userPort.toLocaleLowerCase(),
      ) ?? null
    );
  }, [portsOptions, userPort]);

  const isPrefilledFinalSubmission = Boolean(isFinalSubmission && prefillFromId);

  const prefilledPortRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userPortOption || isRejectedFinalArrivalUpdate) {
      prefilledPortRef.current = null;
      return;
    }
    if (prefilledPortRef.current === userPortOption.value) return;

    prefilledPortRef.current = userPortOption.value;

    formData.setValue(FORM_FIELDS.SELECT_PORT, userPortOption.value, {
      shouldDirty: false,
      shouldValidate: false,
    });
    updateFormData(FORM_FIELDS.SELECT_PORT, userPortOption.value);
  }, [formData, isRejectedFinalArrivalUpdate, updateFormData, userPortOption]);

  const expectedArrivalLocationOptions = useMemo(
    () =>
      ports
        .find((p) => p.portCode === selectedPort)
        ?.fromToLocations?.sort((a, b) => a.locationName.localeCompare(b.locationName))
        .map((l) => ({ label: l.locationName, value: l.fromToLocationCode })) || [],
    [ports, selectedPort],
  );

  const shouldShowAnchorageFields = useMemo(
    () => anchorageOptions.includes(expectedArrivalLocation ?? "") && !isMarineDepartmentUser,
    [expectedArrivalLocation, isMarineDepartmentUser],
  );

  const { options: anchorageAreas } = useFormattedReference<AnchorageArea>(
    REFERENCE_DATA_TYPES.ANCHORAGE_AREAS,
    "bff-vessel-call",
  );

  const anchorageAreasOptions = useMemo(
    () =>
      anchorageAreas
        .filter((area) => area.fromtoloc === expectedArrivalLocation)
        .map((area) => ({ label: area.anchorageAreaDesc, value: area.anchorageAreaCode }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [anchorageAreas, expectedArrivalLocation],
  );

  const clearLinkedPortFields = (fields: (keyof VesselCallFormData)[]) => {
    fields.forEach((field) => {
      formData.setValue(field, "", {
        shouldDirty: true,
        shouldValidate: false,
      });
      updateFormData(field, "");
    });

    formData.clearErrors([...fields]);
  };

  const isSelectPortFieldDisabled =
    Boolean(userPortOption) || isPrefilledFinalSubmission || isRejectedFinalArrivalUpdate;

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${LANG_BASE}.form-sections.port`)}>
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.SELECT_PORT>
          name={FORM_FIELDS.SELECT_PORT}
          type="autocomplete"
          options={portsOptions}
          label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.SELECT_PORT}`)}
          testId="select-port"
          disabled={isSelectPortFieldDisabled}
          onFieldChange={(k, v) => {
            clearLinkedPortFields(LINKED_PORT_FIELDS);
            updateFormData(k, v);
          }}
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.EXPECTED_ARRIVAL_LOCATION>
          name={FORM_FIELDS.EXPECTED_ARRIVAL_LOCATION}
          type="autocomplete"
          options={expectedArrivalLocationOptions}
          label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.EXPECTED_ARRIVAL_LOCATION}`)}
          testId="expected-arrival-location"
          onFieldChange={(k, v) => {
            clearLinkedPortFields([
              FORM_FIELDS.PREFERRED_ANCHORAGE_AREA,
              FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE,
            ]);
            updateFormData(k, v);
          }}
        />
        {shouldShowAnchorageFields && (
          <>
            <FormItem<VesselCallFormData, typeof FORM_FIELDS.PREFERRED_ANCHORAGE_AREA>
              name={FORM_FIELDS.PREFERRED_ANCHORAGE_AREA}
              type="autocomplete"
              options={anchorageAreasOptions}
              label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.PREFERRED_ANCHORAGE_AREA}`)}
              testId="preferred-anchorage-area"
              onFieldChange={(k, v) => {
                if (!v) {
                  formData.trigger(FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE);
                }
                updateFormData(k, v);
              }}
            />
            <div className="col-span-full">
              <FormItem<VesselCallFormData, typeof FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE>
                name={FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE}
                type="text"
                label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE}`)}
                testId="reason-for-anchorage-preference"
                onFieldChange={updateFormData}
                maxChars={100}
              />
            </div>
          </>
        )}
      </FormSection>
    </FormProvider>
  );
};

export default Port;
